import { workflowRunsApi } from '@/api/workflowRuns'
import type { NodeLiveLogsSnapshot, PaginationParams, WorkflowNodeLogs, WorkflowNodeLogsResponse, WorkflowNodeStatus, WorkflowRunFilters, WorkflowRunNode, WorkflowRunNodeMemory } from '@/types'
import { isActiveRunStatus } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isWithinMs } from '@/lib/timeFormatters'
import { useTenantKey } from './useTenantKey'

type TenantKeyFn = ReturnType<typeof useTenantKey>

function scheduleRunRevalidation(queryClient: ReturnType<typeof useQueryClient>, tenantKey: TenantKeyFn, projectId: string, runId: string) {
  const delaysMs = [0, 500, 1500, 3000, 5000, 8000]
  for (const delay of delaysMs) {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs', runId), exact: true })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'detail'), exact: true })
    }, delay)
  }

  setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
  }, 1500)
}

export function useWorkflowRuns(projectId: string, pagination: PaginationParams, filters?: WorkflowRunFilters) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', { ...filters, ...pagination }),
    queryFn: () => workflowRunsApi.getAll(projectId, filters, pagination),
    enabled: !!projectId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data?.items) return false
      const hasActiveRuns = data.items.some(r => isActiveRunStatus(r.status))
      return hasActiveRuns ? 3000 : false
    },
  })
}

export function useWorkflowRunDetail(projectId: string, runId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId!),
    queryFn: () => workflowRunsApi.getById(projectId, runId!),
    enabled: !!projectId && !!runId,
    refetchOnMount: 'always',
  })
}

export function useWorkflowRunsForWorkItem(
  projectId: string,
  workItemId: string,
  pagination?: PaginationParams,
  filters?: WorkflowRunFilters,
) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', workItemId, 'workflow-runs', { ...filters, ...pagination }),
    queryFn: () => workflowRunsApi.getForWorkItem(projectId, workItemId, filters, pagination),
    enabled: !!projectId && !!workItemId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data?.items) return false
      const hasActiveRuns = data.items.some(r => isActiveRunStatus(r.status))
      return hasActiveRuns ? 3000 : false
    },
  })
}

export function useCancelWorkflowRun(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (runId: string) => workflowRunsApi.cancel(projectId, runId),
    onSuccess: (_, runId) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs', runId) })
    },
  })
}

export function useRetryWorkflowRun(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (runId: string) => workflowRunsApi.retry(projectId, runId),
    onSuccess: (_, runId) => {
      scheduleRunRevalidation(queryClient, tenantKey, projectId, runId)
    },
  })
}

export function useRetryNode(projectId: string, runId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ nodeId, modifiedInputsJson }: { nodeId: string; modifiedInputsJson?: string }) =>
      workflowRunsApi.retryNode(projectId, runId, nodeId, modifiedInputsJson),
    onSuccess: () => {
      scheduleRunRevalidation(queryClient, tenantKey, projectId, runId)
    },
  })
}

export function useSkipNode(projectId: string, runId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (nodeId: string) => workflowRunsApi.skipNode(projectId, runId, nodeId),
    onSuccess: () => {
      scheduleRunRevalidation(queryClient, tenantKey, projectId, runId)
    },
  })
}

function resolveLastLiveLogs(fresh: WorkflowNodeLogsResponse, previous: WorkflowNodeLogs | undefined): NodeLiveLogsSnapshot | null {
  if (fresh.liveContainerLogsAvailable && fresh.logs) {
    return { containerId: fresh.containerId, logs: fresh.logs }
  }

  return previous?.lastLiveLogs?.containerId === fresh.containerId ? previous.lastLiveLogs : null
}

export function useNodeLogs(projectId: string, runId: string, nodeId: string | undefined, nodeStatus?: WorkflowNodeStatus) {
  const tenantKey = useTenantKey()
  const queryClient = useQueryClient()
  const queryKey = tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId!, 'logs')

  return useQuery({
    queryKey,
    queryFn: async (): Promise<WorkflowNodeLogs> => {
      const fresh = await workflowRunsApi.getNodeLogs(projectId, runId, nodeId!)
      const previous = queryClient.getQueryData<WorkflowNodeLogs>(queryKey)

      return { ...fresh, lastLiveLogs: resolveLastLiveLogs(fresh, previous) }
    },
    enabled: !!projectId && !!runId && !!nodeId,
    refetchInterval: nodeStatus === 'Running' || nodeStatus === 'RetryPending' ? 5000 : false,
  })
}

type NodeContentVersion = Pick<WorkflowRunNode, 'id' | 'status' | 'retryCount' | 'manualRetryCount'>

export function useWorkflowRunNodeContent(projectId: string, runId: string, node: NodeContentVersion | null) {
  const tenantKey = useTenantKey()
  const nodeId = node?.id
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId!, 'content', node?.status, node?.retryCount, node?.manualRetryCount),
    queryFn: () => workflowRunsApi.getNodeContent(projectId, runId, nodeId!),
    enabled: !!projectId && !!runId && !!nodeId,
    placeholderData: (previous) => (previous?.nodeId === nodeId ? previous : undefined),
  })
}

export function usePersistedNodeLogs(projectId: string, runId: string, nodeId: string | undefined, enabled: boolean) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId!, 'logs', 'persisted'),
    queryFn: () => workflowRunsApi.getPersistedNodeLogs(projectId, runId, nodeId!),
    enabled: enabled && !!projectId && !!runId && !!nodeId,
    staleTime: Infinity,
  })
}

const MEMORY_POLL_INTERVAL_MS = 5000
const MEMORY_POLL_WINDOW_MS = 120_000

function memoryStillArriving(data: WorkflowRunNodeMemory | null | undefined, windowOpen: boolean): boolean {
  if (!data?.isAgentNode || !data.memoryEnabled) return false

  return windowOpen
}

export function useNodeAgentMemory(
  projectId: string,
  runId: string,
  nodeId: string | undefined,
  completedAt?: string | null,
) {
  const tenantKey = useTenantKey()
  const isExtractionWindowOpen = isWithinMs(completedAt, MEMORY_POLL_WINDOW_MS)

  const query = useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId!, 'memory'),
    queryFn: () => workflowRunsApi.getNodeMemory(projectId, runId, nodeId!),
    enabled: !!projectId && !!runId && !!nodeId,
    refetchInterval: (query) =>
      memoryStillArriving(query.state.data, isExtractionWindowOpen) ? MEMORY_POLL_INTERVAL_MS : false,
  })

  return { ...query, isExtractionWindowOpen }
}

export function useNodeErrorSummary(projectId: string, runId: string, nodeId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId!, 'error-summary'),
    queryFn: () => workflowRunsApi.getNodeErrorSummary(projectId, runId, nodeId!),
    enabled: !!projectId && !!runId && !!nodeId,
  })
}
